import { Navigate, useLocation } from 'react-router';
import { useIsMutating } from '@tanstack/react-query';
import { AppSplash } from '@fms/ui';
import { LOGOUT_MUTATION_KEY } from '@fms/api-client';
import { useSession } from '../session';

/** Gate for authed routes. Waits for the silent-restore attempt before
 *  deciding, so a valid session never flashes the login screen. The splash is
 *  fixed and opaque, which also hides the chrome this renders inside — a cold
 *  deep link to a protected route must not paint the shell before we know
 *  whether the visitor may see it. */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, ready } = useSession();
  const location = useLocation();
  // A logout in flight drops `user` before the explicit navigation away has
  // committed — stand down instead of bouncing the user to the login screen.
  const loggingOut = useIsMutating({ mutationKey: LOGOUT_MUTATION_KEY }) > 0;
  if (!ready || loggingOut) return <AppSplash />;
  if (!user)
    return <Navigate to={`/auth/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  // Signed in but the signup questions were never answered — a reload or a deep
  // link during the profile step lands here. Same destination as "not signed
  // in": the login route renders the completion form for this case, and `next`
  // carries the citizen back to where they were headed.
  if (!user.profileComplete)
    return <Navigate to={`/auth/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return <>{children}</>;
}
